<template>
  <div class="login-wrapper" :style="wrapperBgStyle">
    <div class="login-card">
      <!-- LEFT: Login Form -->
      <div class="login-form" style="align-content: center;">
        <div class="text-center mb-4">
          <img
            :src="brand.logo_url || '/signup-img/hot-logo.webp'"
            alt="Brand Logo"
            class="img-fluid logo"
          />
        </div>

        <h2 class="main-heading text-center">Login To Your Account</h2>
        <p class="subtitle">Use your credentials to login</p>

        <form @submit.prevent="onSubmit">
          <div class="form-group">
            <label for="username" class="form-label">Username</label>
            <input
              type="text"
              id="username"
              class="form-control"
              placeholder="Enter your username"
              v-model="form.username"
              required
            />
          </div>
          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <input
              type="password"
              id="password"
              class="form-control"
              placeholder="Enter your password"
              v-model="form.password"
              required
            />
          </div>

          <div class="form-options">
            <div class="checkbox-wrapper"></div>
            <router-link to="/forget_password" class="forgot-link">
              Forgot Password?
            </router-link>
          </div>

          <button type="submit" class="btn-primary">
            LOGIN
          </button>
        </form>

        <div v-if="errors.invalid" class="alert alert-danger">
          {{ errors.invalid }}
        </div>
      </div>

      <!-- RIGHT: Brand copy + Address/Phone (no video) -->
      <div class="info-panel" :style="{ backgroundColor: 'var(--brand-primary, #800000)' }">
        <h3>{{ heading || 'INDUSTRY TRAINING' }}</h3>
        <p>{{ description || defaultCopy }}</p>

        <div class="contact-block">
          <div class="vimeo-wrap">
    <iframe
      v-if="vimeo_id"
      ref="vimeoFrame"
      :src="vimeoEmbedSrc"
      width="100%"
      height="250"
      class="vimeo-iframe"
      style="border-radius: 10px;"
      frameborder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      title="Brand Video"
    ></iframe>

    <button
      v-if="showReplayButton"
      class="vimeo-replay-btn"
      @click="handleReplay"
      aria-label="Play video"
    >
      ▶ Play
    </button>
  </div>


          <!-- <div class="contact-row" v-if="brandAddress">
            <div class="contact-icon"><i class="fa fa-map"></i></div>
            <div class="contact-text">{{ brandAddress }}</div>
          </div> -->

          <!-- Show brand phone ONLY if present; no fallbacks -->
          <!-- <div class="contact-row" v-if="brandPhone">
            <div class="contact-icon"><i class="fas fa-phone-volume"></i></div>
            <div class="contact-text">
              <a :href="telHref">{{ brandPhone }}</a>
            </div>
          </div> -->

          <!-- Keep address fallback if brand didn’t set address -->
          <!-- <div class="contact-row" v-if="!brandAddress">
            <div class="contact-icon"><i class="fa fa-map"></i></div>
            <div class="contact-text">{{ fallbackAddress }}</div>
          </div> -->
        </div>
      </div>
      <!-- /info-panel -->
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";
import { brandContext } from "@/brand/brandContext";

export default {
  name: "BrandLogin",
  props: { slug: { type: String, required: true } },
  data() {
    return {
      loading: false,
      brand: brandContext.state,
      heading: "",
      vimeo_id: "",
      description: "",
      form: { username: "", password: "" },
      errors: { invalid: "" },
      signupRoute: "/new_registration",
      showReplayButton: false,
      vimeoPlayer: null,
      defaultCopy:
`Welcome to your training program. Short, practical courses designed to keep your team safe, compliant, and ready for the work ahead.`
    };
  },
  computed: {
    vimeoEmbedSrc() {
      const id = String(this.vimeo_id || "").trim() || "347119375";
      // No autoplay in the URL, we control it with the API
      return `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&playsinline=1&title=0&byline=0&portrait=0&controls=0`;
    },
    wrapperBgStyle() {
      const url = this.brand?.background_url;
      return {
        backgroundImage: `url('${url || "/boston/loginbg.jpg"}')`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    },
    brandPhone() { return this.brand?.phone || ""; },
    brandAddress() { return this.brand?.address || ""; },
    telHref() {
      if (!this.brandPhone) return "";
      const num = this.brandPhone.replace(/(?!^\+)[^\d]/g, "");
      return `tel:${num}`;
    },
    fallbackAddress() {
      return "420 S. State Street, Dover, DE 19901";
    }
  },
  async created() {
    brandContext.boot?.();
    this.loading = true;
    try {
      const { data } = await this.$http.get(`branding/by-slug/${this.slug}`);
      brandContext.set(data);
      this.brand = brandContext.state;
      this.heading = data.login_heading || `Welcome to ${data.company_name} Training`;
      this.description = data.login_description || this.defaultCopy;
      this.vimeo_id = data.vimeo_id;
      document.querySelector("title").textContent = `${data.company_name} LMS`;
    } catch (e) {
      this.$router.replace("/login");
      return;
    } finally {
      this.loading = false;
    }

    if (this.$route.query.email && this.$route.query.password) {
      this.form.username = this.$route.query.email;
      this.form.password = this.$route.query.password;
      this.onSubmit();
    }
  },
  watch: {
    // Initialize the Vimeo player once the iframe is in the DOM and we have an id
    vimeo_id: {
      immediate: false,
      async handler(val) {
        if (!val) return;
        await this.loadVimeoSDK();
        this.initVimeoPlayer();
      }
    }
  },
  methods: {
    async loadVimeoSDK() {
      if (window.Vimeo && window.Vimeo.Player) return;
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://player.vimeo.com/api/player.js";
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    },
    initVimeoPlayer() {
      const iframe = this.$refs.vimeoFrame;
      if (!iframe || !window.Vimeo?.Player) return;

      this.vimeoPlayer = new window.Vimeo.Player(iframe, {
        autopause: true,
        controls: false,
        loop: false,
        muted: true,          // required for reliable autoplay
        playsinline: true,
        transparent: false
      });

      this.vimeoPlayer.ready().then(async () => {
        try {
          await this.vimeoPlayer.setCurrentTime(0);
          const hasAutoplayed = sessionStorage.getItem("vimeo_autoplay_done") === "1";
          if (!hasAutoplayed) {
            // First visit in this tab, auto play muted
            await this.vimeoPlayer.play();
          } else {
            // Show the first frame and our Play button
            await this.vimeoPlayer.pause();
            this.showReplayButton = true;
          }
        } catch (e) {
          // If autoplay is blocked, show the Play button
          this.showReplayButton = true;
        }
      });

      this.vimeoPlayer.on("ended", async () => {
        try {
          await this.vimeoPlayer.setCurrentTime(0);
          await this.vimeoPlayer.pause();
        } finally {
          sessionStorage.setItem("vimeo_autoplay_done", "1");
          this.showReplayButton = true;
        }
      });
    },
    async handleReplay() {
      if (!this.vimeoPlayer) return;
      this.showReplayButton = false;
      try {
        // User clicked, safe to unmute
        await this.vimeoPlayer.setMuted(false);
        await this.vimeoPlayer.play();
      } catch (e) {
        this.showReplayButton = true;
      }
    },
    onSubmit() {
      this.loading = true;
      this.errors.invalid = "";
      this.$http
        .post("user/login", {
          email: this.form.username,
          password: this.form.password
        })
        .then((resp) => {
          localStorage.setItem("hot-token", resp.data.token);
          localStorage.setItem("hot-user", resp.data.role);
          localStorage.setItem("hot-logged-user", resp.data.user_id);
          localStorage.setItem("hot-user-full-name", resp.data.full_name);
          const headers = {
            authorization: "Bearer " + resp.data.token,
            "content-type": "application/json",
          };
          this.$http.defaults.headers.authorization = "Bearer " + resp.data.token;
          const goDash = () => this.$router.push("/dashboard");
          switch (resp.data.role) {
            case "super-admin":
              localStorage.setItem("hot-sidebar", "super_admin");
              this.$http.post("company/managerdata", { email: this.form.username }, { headers })
                .then(r => { localStorage.setItem("hot-user-id", r.data[0].id); goDash(); });
              break;
            case "company-admin":
              localStorage.setItem("hot-sidebar", "admin");
              this.$http.post("company/data", { email: this.form.username }, { headers })
                .then(r => {
                  localStorage.setItem("hot-company-level", r.data.level ? "parent" : "child");
                  localStorage.setItem("hot-admin-id", r.data.admin_id);
                  localStorage.setItem("hot-user-id", r.data[0].id);
                  localStorage.setItem("hot-company-name", r.data[0].name);
                  goDash();
                });
              break;
            case "manager":
              localStorage.setItem("hot-sidebar", "manager");
              this.$http.post("company/managerdata", { email: this.form.username }, { headers })
                .then(r => {
                  localStorage.setItem("hot-user-id", r.data[0].id);
                  localStorage.setItem("hot-user-name", r.data[0].full_name);
                  localStorage.setItem("hot-user-number", r.data[0].phone_num);
                  localStorage.setItem("hot-user-2fa", r.data[0].is_2f_authenticated);
                  goDash();
                });
              break;
            case "employee":
              localStorage.setItem("hot-sidebar", "employee");
              this.$http.post("employees/user_data", { user_name: this.form.username }, { headers })
                .then(r => {
                  localStorage.setItem("hot-user-id", r.data[0].id);
                  localStorage.setItem("hot-user-name", r.data[0].full_name);
                  localStorage.setItem("hot-user-number", r.data[0].phone_num);
                  localStorage.setItem("hot-user-2fa", r.data[0].is_2f_authenticated);
                  if (r.data[0].employee_status == 0) {
                    this.errors.invalid = "Account is Deactivated by Admin..!!";
                  } else { goDash(); }
                });
              break;
            case "sub-admin":
              localStorage.setItem("hot-sidebar", "sub_admin");
              this.$http.post("company/managerdata", { email: this.form.username }, { headers })
                .then(r => { localStorage.setItem("hot-user-id", r.data[0].id); goDash(); });
              break;
            case "evaluator":
              // CCU evaluation committee: view-only account (employee-backed record).
              localStorage.setItem("hot-sidebar", "employee");
              this.$http.post("employees/user_data", { user_name: this.form.username }, { headers })
                .then(r => {
                  localStorage.setItem("hot-user-id", r.data[0].id);
                  localStorage.setItem("hot-user-name", r.data[0].full_name);
                  goDash();
                });
              break;
            default:
              this.errors.invalid = "Not Valid..!!";
          }
        })
        .catch((error) => {
          let errorText = "Something went wrong! Please try again later.";
          if (error.response && error.response.status === 422) {
            errorText = error.response.data.message;
          }
          Swal.fire({ title: "Error!", html: errorText, icon: "error" });
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>


<style scoped>
.vimeo-wrap {
  position: relative;
}
.vimeo-iframe {
  display: block;
  width: 100%;
  height: 250px;
  border: 0;
  border-radius: 10px;
}
.vimeo-replay-btn {
  position: absolute;
  inset: 0;
  margin: auto;
  height: 56px;
  min-width: 160px;
  max-width: 70%;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(2px);
}
.vimeo-replay-btn:hover {
  background: rgba(0,0,0,0.7);
}

/* Dark overlay over dynamic background */
.login-wrapper:before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: hsl(0deg 0% 0% / 41%) !important;
}

.login-wrapper {
  min-height: 100vh;
  font-family: 'Nunito', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  background: #000; /* fallback while image loads */
}

.login-card {
  max-width: 900px;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.3);
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.login-form {
  flex: 1 1 300px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 2.5rem 1.5rem;
}

.text-center { text-align: center; }
.mb-4 { margin-bottom: 1.5rem; }
.logo { max-height: 80px; width: auto; }
.img-fluid { max-width: 100%; height: auto; }

.main-heading { font-size: 1.75rem; color: #000; font-weight: 700; margin-bottom: 0.5rem; }
.subtitle { text-align: center; color: #666; margin-bottom: 1.5rem; }

.form-group { margin-bottom: 1rem; }
.form-label { display: block; font-weight: 300; margin-bottom: 0.5rem; }

.form-control {
  width: 100%;
  border-radius: 4px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  transition: all 0.3s ease;
}
.form-control:focus {
  outline: none;
  border-color: var(--brand-primary, #800000);
  box-shadow: 0 0 0 0.2rem rgba(19, 44, 90, 0.15);
  background: rgba(255, 255, 255, 0.95);
}
.form-control::placeholder { color: #132c5a; opacity: 0.7; }

.form-options {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 1.5rem; gap: 0.5rem; margin-left: 21px; flex-wrap: wrap;
}

.forgot-link, .signup-link {
  color: var(--brand-primary, #800000);
  text-decoration: none; font-weight: 300; font-size: 0.9rem;
}
.forgot-link:hover, .signup-link:hover { text-decoration: underline; }

.btn-primary {
  background-color: var(--brand-primary, #800000);
  border: none; color: #fff; width: 100%; padding: 0.75rem;
  border-radius: 0.25rem; font-size: 1rem; font-weight: 300;
  cursor: pointer; transition: background-color 0.3s ease; margin-bottom: 1rem;
}
.btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

.signup-text { text-align: center; font-size: 0.9rem; color: #666; margin: 0; }

/* Right panel (brand color background) */
.info-panel {
  flex: 1.2 1 300px;
  color: #fff; padding: 2.5rem 1.5rem; align-content: center;
}
.info-panel h3 { font-size: 1.25rem; margin-bottom: 0.75rem; font-weight: 300; color: #fff; }
.info-panel p { font-size: 0.95rem; line-height: 1.6; margin-bottom: 1.5rem; opacity: 0.95; }

/* Contact rows */
.contact-block { display: grid; gap: 12px; }
.contact-row { display: flex; gap: 10px; align-items: center; }
.contact-icon i { font-size: 20px; color: #fff; opacity: 0.9; }
.contact-text, .contact-text a { color: #fff; }
.contact-text a { text-decoration: none; }
.contact-text a:hover { text-decoration: underline; }

.alert { padding: 0.75rem 1rem; margin-top: 1rem; border-radius: 0.25rem; }
.alert-danger { background-color: #f8d7da; border: 1px solid #f5c6cb; color: #721c24; }

/* Responsive tweaks */
@media (max-width: 767px) {
  /* shrink the whole card ~15% on phones so more background is visible */
  .login-card {
    transform: scale(0.85);
    transform-origin: top center;
  }
  /* lighten the overlay so background shows more on phones */
  .login-wrapper:before {
    background-color: hsl(0deg 0% 0% / 28%) !important;
  }
  .info-panel { margin-top: 1rem; order: 1; }
  .login-form, .info-panel { padding: 1.5rem 1rem; }
  .login-wrapper { padding: 0.5rem; }
  .form-options { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
}

@media (max-width: 576px) {
  /* a touch smaller (~20%) on extra small screens */
  .login-card { transform: scale(0.80); margin-top: 10px ; }
  .main-heading { font-size: 1.4rem; }
  .logo { max-height: 56px; }
}
</style>
